import PropTypes from 'prop-types';
import React from 'react';
import _isEmpty from 'lodash/isEmpty';

import { calculateLastPage } from 'ravenjs/utils/pagination';

import BannerPaginationFooter from './BannerPaginationFooter';
import PaginationBarNav from './PaginationBarNav';
import PaginationBarRange from './PaginationBarRange';
import PaginationBarRowsPerPage from './PaginationBarRowsPerPage';
import { PaginationBarContainer, PaginationBarGroup } from './styles';

/*
 * Quartz-styled pagination footer (GN-37779, Banner Screen redesign).
 *
 * When the Banner Screen redesign feature flag is enabled, this renders the
 * redesigned three-zone footer (`Result: N` summary / first-prev-page-next-last
 * navigation / `Go to Page` input) matching the attached Figma specs. When the
 * flag is off it falls back to the earlier layout (rows-per-page selector +
 * "x-y of total" range + ‹ › chevrons). Last-page math reuses
 * `calculateLastPage`, the same helper `PaginationActions` uses.
 *
 * This is opt-in only — it is rendered by `ravenjs/lib/Pagination` when the
 * `useQuartzPagination` flag is set, leaving the legacy `Pagination` and
 * `PaginationV2` paths untouched for every existing consumer.
 */
function PaginationBar({
    currentPage,
    rowsPerPage,
    rowsPerPageOptions,
    enableRowsPerPageOptions,
    totalRows,
    onChangePage,
    onChangeRowsPerPage,
    onRowsChange,
    rowsPerPageLabel,
    menuPlacement,
    style,
}) {
    const numericTotal = Number(totalRows) || 0;
    const lastPage = calculateLastPage(numericTotal, rowsPerPage);

    // Resolved lazily at render (which always happens inside a store-backed
    // context) so this shared `ravenjs/lib` component does not eagerly pull the
    // app-level redux store in through `utils/company` at module-load time.
    // A static import made every file that imports the `ravenjs/lib` barrel
    // build the store on require, breaking unrelated suites.
    // eslint-disable-next-line global-require
    const { isBannerScreenRedesignFeatureEnabled } = require('utils/company');

    if (isBannerScreenRedesignFeatureEnabled()) {
        return (
            <BannerPaginationFooter
                currentPage={currentPage}
                onChangePage={onChangePage}
                rowsPerPage={rowsPerPage}
                style={style}
                totalRows={numericTotal}
            />
        );
    }

    const handleRowsChange = option => {
        if (typeof onRowsChange === 'function') {
            onRowsChange(option);
        } else {
            onChangeRowsPerPage({ value: Number.parseInt(option.value, 10) });
        }
    };

    const handlePrev = event => onChangePage(event, currentPage - 1);
    const handleNext = event => onChangePage(event, currentPage + 1);

    const showRowsPerPage = enableRowsPerPageOptions && !_isEmpty(rowsPerPageOptions);

    // `DataView` injects `style: { padding: '20px 0' }` for the legacy
    // pagination layout. Drop that padding here so the Quartz footer keeps its
    // own (`12px 16px`) spacing without touching `DataView` globally.
    const { padding, ...containerStyle } = style || {};

    return (
        <PaginationBarContainer style={containerStyle}>
            <PaginationBarGroup>
                {showRowsPerPage && (
                    <PaginationBarRowsPerPage
                        label={rowsPerPageLabel}
                        menuPlacement={menuPlacement}
                        onChange={handleRowsChange}
                        rowsPerPage={rowsPerPage}
                        rowsPerPageOptions={rowsPerPageOptions}
                    />
                )}
                <PaginationBarRange
                    currentPage={currentPage}
                    rowsPerPage={rowsPerPage}
                    totalRows={numericTotal}
                />
            </PaginationBarGroup>
            <PaginationBarNav
                currentPage={currentPage}
                lastPage={lastPage}
                onNext={handleNext}
                onPrev={handlePrev}
            />
        </PaginationBarContainer>
    );
}

PaginationBar.propTypes = {
    currentPage: PropTypes.number,
    enableRowsPerPageOptions: PropTypes.bool,
    menuPlacement: PropTypes.string,
    onChangePage: PropTypes.func,
    onChangeRowsPerPage: PropTypes.func,
    onRowsChange: PropTypes.func,
    rowsPerPage: PropTypes.number,
    rowsPerPageLabel: PropTypes.node,
    rowsPerPageOptions: PropTypes.array,
    style: PropTypes.object,
    totalRows: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

PaginationBar.defaultProps = {
    currentPage: 1,
    enableRowsPerPageOptions: false,
    menuPlacement: 'auto',
    onChangePage: () => {},
    onChangeRowsPerPage: () => {},
    onRowsChange: null,
    rowsPerPage: 10,
    rowsPerPageLabel: 'Rows per page:',
    rowsPerPageOptions: [5, 10, 15, 20],
    style: null,
    totalRows: 0,
};

export default PaginationBar;
